import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import api from "../../../../chat/api";
import { FilterChevronIcon } from "./svgIcons";
import CommentTab from "./CommentTab";
import ReviewStatusTab from "./ReviewStatusTab";

const RightSection = ({
  activeRightSection,
  rightViewTab,
  setRightViewTab,
  filterType,
  setFilterType,
  isDropdownOpen,
  setIsDropdownOpen,
  displayedMembers,
  expandedMemberIds,
  toggleMemberCard,
  getInitials,
  orgId,
  folderId,
  onRefresh,
  userPermission,
}) => {
  const [commentInputs, setCommentInputs] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [successId, setSuccessId] = useState(null);
  const [resolvingIds, setResolvingIds] = useState({});
  const [resolveSuccessIds, setResolveSuccessIds] = useState({});
  const [currentUserEmail, setCurrentUserEmail] = useState("");

  useEffect(() => {
    const storedEmail = localStorage.getItem("email");
    if (storedEmail) {
      setCurrentUserEmail(storedEmail);
    }
  }, []);

  const canResolve =
    userPermission !== "reviewer" && userPermission !== "editor";

  const handleCommentChange = (userId, text) => {
    setCommentInputs((prev) => ({
      ...prev,
      [userId]: text,
    }));
  };

  const handleAddComment = async (memberUserId) => {
    const commentText = commentInputs[memberUserId];

    if (!commentText || !commentText.trim()) return;
    if (!orgId || !folderId || !activeRightSection) {
      toast.error("Missing required project information");
      return;
    }

    setLoadingId(memberUserId);

    try {
      const payload = {
        org_id: parseInt(orgId),
        section_id: activeRightSection.section_id || activeRightSection.id,
        user_id: memberUserId,
        comment: commentText,
      };

      await api.post(
        `/api/v1/rfp/folders/${folderId}/section-view/add-comment/`,
        payload
      );

      setLoadingId(null);
      setSuccessId(memberUserId);

      setCommentInputs((prev) => ({
        ...prev,
        [memberUserId]: "",
      }));

      setTimeout(() => {
        setSuccessId(null);
        if (onRefresh) {
          onRefresh();
        }
      }, 2000);
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
      setLoadingId(null);
    }
  };

  const handleResolveToggle = async (memberUserId, currentStatus) => {
    if (!orgId || !folderId || !activeRightSection) {
      toast.error("Missing required project information");
      return;
    }

    const newStatus = !currentStatus;

    setResolvingIds((prev) => ({ ...prev, [memberUserId]: true }));

    try {
      const payload = {
        org_id: parseInt(orgId),
        section_id: activeRightSection.section_id || activeRightSection.id,
        user_id: memberUserId,
        resolved: newStatus,
      };

      await api.post(
        `/api/v1/rfp/folders/${folderId}/section-view/resolve-comment/`,
        payload
      );

      setResolvingIds((prev) => ({ ...prev, [memberUserId]: false }));

      if (newStatus === true) {
        setResolveSuccessIds((prev) => ({ ...prev, [memberUserId]: true }));

        if (onRefresh) {
          await onRefresh();
        }

        setTimeout(() => {
          setResolveSuccessIds((prev) => ({
            ...prev,
            [memberUserId]: false,
          }));
        }, 2000);
      } else {
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error) {
      console.error("Error resolving comment:", error);
      toast.error("Failed to update resolution status");
      setResolvingIds((prev) => ({ ...prev, [memberUserId]: false }));
    }
  };

  return (
    <div className="w-[35%] flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="p-6 pb-0">
          <div className="flex items-center justify-between mb-4 relative z-50">
            <h2 className="text-[#18101C] font-outfit text-lg font-semibold">
              Section:{" "}
              {activeRightSection?.title
                ? activeRightSection.title.length > 20
                  ? activeRightSection.title.substring(0, 20) + "..."
                  : activeRightSection.title
                : "None Selected"}
            </h2>

            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 text-[#374151] font-outfit text-sm font-medium bg-[#F9FAFB] border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#C95EBE] focus:border-transparent"
              >
                <span>{filterType}</span>
                <FilterChevronIcon
                  className={`transform transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsDropdownOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-36 bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-100 z-20 p-2 flex flex-col gap-1 animate-in fade-in zoom-in-95 duration-100">
                    {["All", "Editor", "Reviewer"].map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          setFilterType(option);
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-outfit transition-all ${
                          filterType === option
                            ? "bg-white border border-gray-200 font-medium text-[#18101C] shadow-sm"
                            : "text-[#6B7280] hover:bg-gray-50 hover:text-[#374151] border border-transparent"
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center border-b border-gray-200 mb-4">
            <button
              onClick={() => setRightViewTab("comments")}
              className={`flex-1 pb-1 text-[14px] font-medium text-center transition-colors relative ${
                rightViewTab === "comments"
                  ? "text-[#C95EBE]"
                  : "text-[#6B7280] hover:text-[#374151]"
              }`}
            >
              Comments
              {rightViewTab === "comments" && (
                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#C95EBE]" />
              )}
            </button>
            <button
              onClick={() => setRightViewTab("review-status")}
              className={`flex-1 pb-1 text-[14px] font-medium text-center transition-colors relative ${
                rightViewTab === "review-status"
                  ? "text-[#C95EBE]"
                  : "text-[#6B7280] hover:text-[#374151]"
              }`}
            >
              Review Status
              {rightViewTab === "review-status" && (
                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#C95EBE]" />
              )}
            </button>
          </div>
        </div>

        {rightViewTab === "comments" && (
          <CommentTab
            activeRightSection={activeRightSection}
            filterType={filterType}
            displayedMembers={displayedMembers}
            expandedMemberIds={expandedMemberIds}
            toggleMemberCard={toggleMemberCard}
            getInitials={getInitials}
            commentInputs={commentInputs}
            loadingId={loadingId}
            successId={successId}
            resolvingIds={resolvingIds}
            resolveSuccessIds={resolveSuccessIds}
            canResolve={canResolve}
            handleCommentChange={handleCommentChange}
            handleAddComment={handleAddComment}
            handleResolveToggle={handleResolveToggle}
            userPermission={userPermission} 
            currentUserEmail={currentUserEmail} 
          />
        )}

        {rightViewTab === "review-status" && (
          <ReviewStatusTab
            activeRightSection={activeRightSection}
            filterType={filterType}
            displayedMembers={displayedMembers}
            expandedMemberIds={expandedMemberIds}
            toggleMemberCard={toggleMemberCard}
            getInitials={getInitials}
            orgId={orgId}
            folderId={folderId}
            onRefresh={onRefresh}
            userPermission={userPermission}
            currentUserEmail={currentUserEmail}
          />
        )}
      </div>
    </div>
  );
};

export default RightSection;
